import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import { formatDateTime } from "../../lib/format"
import { AppShell } from "../ui/AppShell"
import { Button } from "../ui/Button"
import { Card } from "../ui/Card"
import { Spinner } from "../ui/Spinner"
import { useToast } from "../ui/Toast"
import { statusPill } from "./status"
import type { SupportTicketResponse, SupportTicketsResponse, SupportTicketSummary } from "../../types"

export default function Suporte() {
  const [tickets, setTickets] = useState<SupportTicketSummary[] | null>(null)
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [sending, setSending] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    api
      .get<SupportTicketsResponse>("/api/support_tickets")
      .then((data) => setTickets(data.tickets))
      .catch(() => setTickets([]))
  }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!subject.trim() || !message.trim()) return
    setSending(true)
    try {
      const data = await api.post<SupportTicketResponse>("/api/support_tickets", {
        support_ticket: { subject, message },
      })
      const t = data.ticket
      setTickets((prev) => [
        { id: t.id, subject: t.subject, status: t.status, created_at: t.created_at },
        ...(prev ?? []),
      ])
      setSubject("")
      setMessage("")
      toast("Chamado enviado — responderemos por e-mail.", "success")
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Não foi possível enviar. Tente de novo.", "error")
    } finally {
      setSending(false)
    }
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Suporte</h1>
          <p className="mt-0.5 text-sm text-navy-500">Abra um chamado e acompanhe as respostas aqui.</p>
        </div>

        <Card>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-navy-500">Abrir chamado</h2>
          <form onSubmit={submit} className="flex flex-col gap-3">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={255}
              required
              placeholder="Assunto"
              className="rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-navy-900 outline-none focus:border-gold-400"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              required
              rows={5}
              placeholder="Descreva sua dúvida ou problema..."
              className="rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-navy-900 outline-none focus:border-gold-400"
            />
            <Button type="submit" disabled={sending || !subject.trim() || !message.trim()} className="self-start">
              {sending && <Spinner className="h-4 w-4 text-white" />}
              Enviar chamado
            </Button>
          </form>
        </Card>

        <section aria-label="Meus chamados">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-navy-500">Meus chamados</h2>
          {tickets === null ? (
            <Card className="flex justify-center py-8">
              <Spinner />
            </Card>
          ) : tickets.length === 0 ? (
            <Card className="py-8 text-center">
              <p className="text-sm text-navy-500">Você ainda não abriu nenhum chamado.</p>
            </Card>
          ) : (
            <div className="flex flex-col gap-2">
              {tickets.map((t) => (
                <Link key={t.id} to={`/suporte/${t.id}`}>
                  <Card className="flex items-center justify-between gap-3 p-4! transition-colors hover:border-gold-400">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-navy-900">{t.subject}</p>
                      <p className="text-xs text-navy-400">{formatDateTime(t.created_at)}</p>
                    </div>
                    {statusPill(t.status)}
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  )
}
